import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { AlertTriangle, CalendarCheck2, CalendarClock, CalendarX2, ChevronDown, History, Inbox, Lock, Sparkles, XCircle } from "lucide-react";

import { Badge } from "@/lib/shadcn/ui/badge";
import { Button } from "@/lib/shadcn/ui/button";
import { Card, CardContent, CardHeader } from "@/lib/shadcn/ui/card";
import { Input } from "@/lib/shadcn/ui/input";
import { Skeleton } from "@/lib/shadcn/ui/skeleton";
import { ReservationHistoryDialog } from "@/lib/shadcn/components/reservation-history-dialog";
import { Platform } from "@/models";
import { DigestBlockItem, DigestBlocks, DigestChange, DigestDay, DigestItem, DigestTotals } from "@/models/responses";
import { formatDateToISODate, parseIsoDateOnly } from "@/utils/dates";
import { reasonLabel } from "@/utils/blocks";
import { useGetReservationDigestQuery } from "@/store/booking-management-api";

/** Identifies the reservation whose history timeline the dialog should show. */
type HistoryTarget = { reservationId: number; reservationCode: string };
type OpenHistory = (reservationId: number, reservationCode: string) => void;

type RangeEnd = "startDate" | "endDate";
type DigestRange = Record<RangeEnd, string>;

/** Inclusive default window: last 14 calendar days (today back to today − 13). */
const DEFAULT_WINDOW_DAYS = 14;

/**
 * The API refuses a wider window (it allocates one bucket per calendar day), so
 * the controls are bounded to it — a rejected range would otherwise surface as
 * the generic error panel with a Retry that re-sends it.
 */
const MAX_WINDOW_DAYS = 366;

function shiftIsoDate(value: string, days: number): string {
    const shifted = parseIsoDateOnly(value);
    if (!shifted) {
        return value;
    }
    shifted.setDate(shifted.getDate() + days);
    return formatDateToISODate(shifted);
}

/**
 * Keeps the pair inside MAX_WINDOW_DAYS by pulling the OTHER endpoint along.
 * A native min/max stops the calendar widget but not a typed date, and a range
 * the API refuses would surface as the generic error panel whose Retry re-sends
 * it — so the invalid state is never entered rather than reported.
 */
function boundedRange(startDate: string, endDate: string, moved: RangeEnd): DigestRange {
    // A date input reports "" until its value is a complete valid date; leave a
    // range we cannot read alone rather than rewriting it under the user.
    const start = parseIsoDateOnly(startDate);
    const end = parseIsoDateOnly(endDate);
    if (!start || !end) {
        return { startDate, endDate };
    }
    if (end < start) {
        if (moved === "startDate") {
            return { startDate, endDate: startDate };
        }
        return { startDate: endDate, endDate };
    }
    const widest = shiftIsoDate(startDate, MAX_WINDOW_DAYS - 1);
    if (end <= parseIsoDateOnly(widest)!) {
        return { startDate, endDate };
    }
    if (moved === "startDate") {
        return { startDate, endDate: widest };
    }
    return { startDate: shiftIsoDate(endDate, -(MAX_WINDOW_DAYS - 1)), endDate };
}

/** Names the endpoint that was NOT edited, for the adjustment announcement. */
function otherEnd(moved: RangeEnd): RangeEnd {
    return moved === "startDate" ? "endDate" : "startDate";
}

const RANGE_END_LABEL: Record<RangeEnd, string> = { startDate: "From date", endDate: "To date" };

function defaultRange(): DigestRange {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - (DEFAULT_WINDOW_DAYS - 1));
    return { startDate: formatDateToISODate(start), endDate: formatDateToISODate(end) };
}

/** €620 → "€620", €124.5 → "€124.50". Guards null/NaN → "—". */
function euro(value: number | undefined | null): string {
    if (value === undefined || value === null || Number.isNaN(value)) return "—";
    return `€${value.toFixed(2)}`;
}

/** Localised long day label, e.g. "Sunday, 7 June 2026". */
function dayLabel(v: Date | string): string {
    const d = v instanceof Date ? v : new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function shortDate(v: Date | string): string {
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
}

/** Localised HH:MM for the "Last synced" freshness hint, "—" when absent/invalid. */
function shortTime(v: Date | string | null | undefined): string {
    if (v === null || v === undefined) return "—";
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function platformName(platform: number): string {
    return Platform[platform] ?? "—";
}

/**
 * A day is "active" when any of its three category buckets is non-empty OR it
 * carries at least one owner/maintenance block (added or removed). Routine
 * housekeeping blocks (`blocks.housekeepingCount`) deliberately do NOT make a
 * day active — a housekeeping-only day stays hidden to keep the feed calm
 * (ui-spec "Digest day visibility").
 */
function isActive(day: DigestDay): boolean {
    const ownerMaintenanceBlocks = day.blocks.added.length + day.blocks.removed.length;
    return (
        day.newBookings.length > 0 ||
        day.modifications.length > 0 ||
        day.cancellations.length > 0 ||
        ownerMaintenanceBlocks > 0
    );
}

type Category = "new" | "modifications" | "cancellations";

const CATEGORY_META: Record<
    Category,
    { title: string; badgeLabel: string; badgeClass: string; icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }> }
> = {
    // Colour language mirrors the history dialog: green for created, neutral for
    // a sync/modification, red for a cancellation. Each badge carries TEXT so it
    // is never colour-only (AS-18).
    new: { title: "New bookings", badgeLabel: "New", badgeClass: "border-transparent bg-green-600 text-white", icon: CalendarCheck2 },
    modifications: { title: "Modifications", badgeLabel: "Modified", badgeClass: "", icon: CalendarClock },
    cancellations: { title: "Cancellations", badgeLabel: "Cancelled", badgeClass: "border-transparent bg-red-700 text-white", icon: CalendarX2 },
};

const DigestSkeleton: React.FC = () => (
    <div data-testid="digest-skeleton" className="space-y-4" aria-hidden="true">
        {[0, 1].map((i) => (
            <div key={i} className="space-y-3 rounded-md border p-4">
                <Skeleton className="h-5 w-56" />
                <Skeleton className="h-3 w-72" />
                <Skeleton className="h-3 w-64" />
                <Skeleton className="h-3 w-48" />
            </div>
        ))}
    </div>
);

/** The expanded field-change detail of a modification ("Guests 1 → 2", …). */
const ChangeList: React.FC<{ reservationId: number; changes: DigestChange[] }> = ({ reservationId, changes }) => (
    <ul data-testid={`digest-changes-${reservationId}`} className="mt-2 space-y-1 border-t pt-2">
        {changes.map((c) => (
            <li key={c.field} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{c.field}</span> {c.from} → {c.to}
            </li>
        ))}
    </ul>
);

/** A single reservation row inside a category section ("Recent Reservations"). */
const DigestItemRow: React.FC<{ item: DigestItem; onOpenHistory: OpenHistory }> = ({ item, onOpenHistory }) => {
    const [expanded, setExpanded] = useState(false);
    const changes = item.changes ?? [];
    const hasChanges = changes.length > 0;
    return (
        <li
            data-testid={`digest-item-${item.reservationId}`}
            className="flex flex-col gap-1 rounded-md border p-3"
        >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{item.apartmentName}</span>
                        <span className="text-sm text-muted-foreground">{item.guestName}</span>
                        <span className="text-xs text-muted-foreground">{platformName(item.platform)}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                        Check-in {shortDate(item.checkIn)} · {item.nights} nights · ADR {euro(item.adr)}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium tabular-nums">{euro(item.receivableIncome)}</span>
                    {hasChanges && (
                        <button
                            type="button"
                            data-testid={`digest-changes-toggle-${item.reservationId}`}
                            aria-expanded={expanded}
                            aria-controls={`digest-changes-${item.reservationId}`}
                            onClick={() => setExpanded((v) => !v)}
                            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                        >
                            {changes.length} {changes.length === 1 ? "change" : "changes"}
                            <ChevronDown
                                className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
                                aria-hidden={true}
                            />
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid={`digest-history-link-${item.reservationId}`}
                        onClick={() => onOpenHistory(item.reservationId, item.reservationCode)}
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                    >
                        <History className="h-3 w-3" aria-hidden={true} />
                        View history
                    </button>
                </div>
            </div>
            {hasChanges && expanded && <ChangeList reservationId={item.reservationId} changes={changes} />}
        </li>
    );
};

interface CategorySectionProps {
    category: Category;
    items: DigestItem[];
    onOpenHistory: OpenHistory;
}

const CategorySection: React.FC<CategorySectionProps> = ({ category, items, onOpenHistory }) => {
    const meta = CATEGORY_META[category];
    const Icon = meta.icon;
    const sectionTestId = category === "new" ? "digest-section-new" : `digest-section-${category}`;
    const badgeTestId = category === "new" ? "digest-badge-new" : `digest-badge-${category}`;
    return (
        <section data-testid={sectionTestId} className="space-y-2">
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-muted-foreground" aria-hidden={true} />
                <h3 className="text-sm font-semibold">{meta.title}</h3>
                <Badge
                    data-testid={badgeTestId}
                    variant={meta.badgeClass ? "default" : "secondary"}
                    className={meta.badgeClass || undefined}
                >
                    {meta.badgeLabel}
                </Badge>
                <span className="text-xs text-muted-foreground">({items.length})</span>
            </div>
            {items.length > 0 ? (
                <ul className="space-y-2">
                    {items.map((item) => (
                        <DigestItemRow key={item.versionId} item={item} onOpenHistory={onOpenHistory} />
                    ))}
                </ul>
            ) : (
                <p className="text-xs text-muted-foreground">None</p>
            )}
        </section>
    );
};

/** `HostHub` (navy) / `Manual` (gray) text badge — never colour-only (AS-18). */
const OriginBadge: React.FC<{ origin: string }> = ({ origin }) => {
    const isHostHub = origin === "hosthub";
    return (
        <Badge
            data-testid={`digest-block-origin-${origin}`}
            variant={isHostHub ? "default" : "secondary"}
            className={isHostHub ? "border-transparent bg-slate-800 text-white" : undefined}
        >
            {isHostHub ? "HostHub" : "Manual"}
        </Badge>
    );
};

/**
 * One owner/maintenance block row. Mirrors `DigestItemRow`'s responsive
 * `flex-col sm:flex-row` layout. A removed block (status `block_removed`) is
 * de-emphasised with muted opacity. Added=green, Removed=red, conflict=amber —
 * every status carries TEXT, not colour alone (AS-18).
 */
const BlockRow: React.FC<{ block: DigestBlockItem }> = ({ block }) => {
    const removed = block.status === "block_removed";
    return (
        <li
            data-testid={`digest-block-row-${block.blockId}`}
            className={`flex flex-col gap-1 rounded-md border p-3 ${removed ? "opacity-60" : ""}`}
        >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{block.apartmentName}</span>
                        <span className="text-sm text-muted-foreground">{reasonLabel(block.reason, block.title)}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {shortDate(block.startDate)} → {shortDate(block.endDate)} · {block.nights} nights
                    </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <OriginBadge origin={block.origin} />
                    <Badge
                        data-testid={`digest-block-status-${block.blockId}`}
                        variant="default"
                        className={
                            removed
                                ? "border-transparent bg-red-700 text-white"
                                : "border-transparent bg-green-600 text-white"
                        }
                    >
                        {removed ? "Removed" : "Added"}
                    </Badge>
                    {block.hasConflict && (
                        <Badge
                            data-testid={`digest-conflict-badge-${block.blockId}`}
                            variant="default"
                            className="border-transparent bg-amber-700 text-white"
                        >
                            <AlertTriangle className="mr-1 h-3 w-3" aria-hidden={true} />
                            Overlap conflict
                        </Badge>
                    )}
                </div>
            </div>
            {block.hasConflict && (
                <p className="text-xs text-amber-900">
                    Overlaps a manual block on the same apartment — review in HostHub.
                </p>
            )}
        </li>
    );
};

/**
 * The collapsible "N housekeeping blocks" summary. Collapsed by default; the
 * toggle uses the same `aria-expanded` + `aria-controls` pattern as the
 * modification "N changes" toggle. Routine turnovers are not listed
 * individually unless the operator expands them.
 */
const HousekeepingSummary: React.FC<{ date: string; count: number }> = ({ date, count }) => {
    const [expanded, setExpanded] = useState(false);
    const listId = `digest-housekeeping-list-${date}`;
    return (
        <div data-testid={`digest-housekeeping-summary-${date}`} className="flex flex-col gap-2">
            <button
                type="button"
                data-testid={`digest-housekeeping-toggle-${date}`}
                aria-expanded={expanded}
                aria-controls={listId}
                onClick={() => setExpanded((v) => !v)}
                className="inline-flex w-fit items-center gap-2 rounded-md border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
            >
                <Sparkles className="h-3 w-3" aria-hidden={true} />
                {count} housekeeping {count === 1 ? "block" : "blocks"}
                <span className="sr-only">{expanded ? ", hide" : ", show"}</span>
                <ChevronDown
                    className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`}
                    aria-hidden={true}
                />
            </button>
            {expanded && (
                <p id={listId} className="text-xs text-muted-foreground">
                    {count} routine housekeeping {count === 1 ? "turnover" : "turnovers"} this day.
                </p>
            )}
        </div>
    );
};

/**
 * The full-width per-day Blocks section, rendered below the 3-column category
 * grid. Owner/maintenance blocks (added + removed) list individually; routine
 * housekeeping collapses to a single expandable summary line. Parallel to
 * `CategorySection`, with a labelled `<h3>Blocks</h3>` heading (AS-18).
 */
const DigestBlocksSection: React.FC<{ date: string; blocks: DigestBlocks }> = ({ date, blocks }) => {
    const rows = [...blocks.added, ...blocks.removed];
    return (
        <section data-testid={`digest-blocks-section-${date}`} className="space-y-2">
            <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" aria-hidden={true} />
                <h3 className="text-sm font-semibold">Blocks</h3>
                <span className="text-xs text-muted-foreground">({rows.length})</span>
            </div>
            {rows.length > 0 && (
                <ul className="space-y-2">
                    {rows.map((block) => (
                        <BlockRow key={`${block.blockId}-${block.versionId}-${block.status}`} block={block} />
                    ))}
                </ul>
            )}
            {blocks.housekeepingCount > 0 && <HousekeepingSummary date={date} count={blocks.housekeepingCount} />}
        </section>
    );
};

const DayTotals: React.FC<{ date: string; totals: DigestTotals }> = ({ date, totals }) => (
    <div data-testid={`digest-day-totals-${date}`} className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
        <span>
            <span className="font-medium text-foreground">{totals.newBookingsCount}</span> new
        </span>
        <span>
            <span className="font-medium text-foreground">{totals.modificationsCount}</span> modified
        </span>
        <span>
            <span className="font-medium text-foreground">{totals.cancellationsCount}</span> cancelled
        </span>
        <span>
            <span className="font-medium text-foreground">{euro(totals.revenue)}</span> revenue
        </span>
        <span>
            <span className="font-medium text-foreground">{totals.nights}</span> nights
        </span>
        <span>
            <span className="font-medium text-foreground">{totals.cancelledNights ?? 0}</span> cancelled nights
        </span>
        {totals.blocksCount > 0 && (
            <span>
                <span className="font-medium text-foreground">{totals.blocksCount}</span> blocks
            </span>
        )}
    </div>
);

const DayCard: React.FC<{ day: DigestDay; onOpenHistory: OpenHistory }> = ({ day, onOpenHistory }) => {
    const date = formatDateToISODate(day.date instanceof Date ? day.date : new Date(day.date));
    return (
        <Card data-testid={`digest-day-${date}`} className="bg-white">
            <CardHeader className="gap-1">
                <h2 className="text-xl font-bold leading-none">{dayLabel(day.date)}</h2>
                <DayTotals date={date} totals={day.totals} />
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                <div className="grid gap-4 md:grid-cols-3">
                    <CategorySection category="new" items={day.newBookings} onOpenHistory={onOpenHistory} />
                    <CategorySection category="modifications" items={day.modifications} onOpenHistory={onOpenHistory} />
                    <CategorySection category="cancellations" items={day.cancellations} onOpenHistory={onOpenHistory} />
                </div>
                {(day.blocks.added.length > 0 ||
                    day.blocks.removed.length > 0 ||
                    day.blocks.housekeepingCount > 0) && <DigestBlocksSection date={date} blocks={day.blocks} />}
            </CardContent>
        </Card>
    );
};

/**
 * DailyDigestPage (spec reservation-daily-digest, AS-12..AS-18) — an admin
 * "today view": a calm day-by-day activity feed of new bookings, modifications
 * and cancellations, newest day first. The headline summarises the window
 * totals; each day card shows its own totals + the three category sections.
 * All money/nights come straight from the API (no client-side recompute).
 */
const DailyDigestPage: React.FC = () => {
    const [range, setRange] = useState(defaultRange);
    const [rangeAdjustment, setRangeAdjustment] = useState("");
    const [history, setHistory] = useState<HistoryTarget | null>(null);

    // Editing one endpoint can move the other (boundedRange). That is invisible to
    // anyone not watching both fields, so the adjustment is announced.
    const changeRangeEnd = useCallback(
        (moved: RangeEnd, value: string) => {
            const bounded = boundedRange(
                moved === "startDate" ? value : range.startDate,
                moved === "endDate" ? value : range.endDate,
                moved,
            );
            const pulled = otherEnd(moved);
            setRangeAdjustment(
                bounded[pulled] === range[pulled]
                    ? ""
                    : `${RANGE_END_LABEL[pulled]} adjusted to ${bounded[pulled]} to stay within ${MAX_WINDOW_DAYS} days`,
            );
            setRange(bounded);
        },
        [range],
    );

    const openHistory = useCallback<OpenHistory>(
        (reservationId, reservationCode) => setHistory({ reservationId, reservationCode }),
        [],
    );
    const closeHistory = useCallback(() => setHistory(null), []);

    const { data, isLoading, isError, refetch } = useGetReservationDigestQuery(range);

    // Newest day first. The backend already returns newest-first, but we sort
    // defensively so display order is deterministic.
    const days = useMemo(() => {
        const list = data?.days ?? [];
        return [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [data]);

    const activeDays = useMemo(() => days.filter(isActive), [days]);

    const rangeTotals = data?.rangeTotals;
    const windowDays = useMemo(() => {
        const start = new Date(range.startDate).getTime();
        const end = new Date(range.endDate).getTime();
        if (Number.isNaN(start) || Number.isNaN(end)) return DEFAULT_WINDOW_DAYS;
        return Math.round((end - start) / 86400000) + 1;
    }, [range]);

    const headline = rangeTotals
        ? `${rangeTotals.newBookingsCount} ${rangeTotals.newBookingsCount === 1 ? "booking" : "bookings"} in the last ${windowDays} days brought in ${euro(rangeTotals.revenue)}`
        : "";

    return (
        <div data-testid="daily-digest-page" className="flex flex-col gap-4">
            <h1 className="text-3xl font-bold">Daily Digest</h1>

            {/* Range control — native date inputs are keyboard-operable and
                labelled; changing either re-queries via the RTK cache key. */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                    <label htmlFor="digest-start-date" className="text-xs font-medium text-muted-foreground">
                        From
                    </label>
                    <Input
                        id="digest-start-date"
                        data-testid="digest-start-date"
                        type="date"
                        value={range.startDate}
                        min={shiftIsoDate(range.endDate, -(MAX_WINDOW_DAYS - 1))}
                        max={range.endDate}
                        className="w-auto"
                        aria-describedby="digest-range-hint"
                        onChange={(e) => changeRangeEnd("startDate", e.target.value)}
                    />
                </div>
                <div className="flex flex-col gap-1">
                    <label htmlFor="digest-end-date" className="text-xs font-medium text-muted-foreground">
                        To
                    </label>
                    <Input
                        id="digest-end-date"
                        data-testid="digest-end-date"
                        type="date"
                        value={range.endDate}
                        min={range.startDate}
                        max={shiftIsoDate(range.startDate, MAX_WINDOW_DAYS - 1)}
                        className="w-auto"
                        aria-describedby="digest-range-hint"
                        onChange={(e) => changeRangeEnd("endDate", e.target.value)}
                    />
                </div>

                <span id="digest-range-hint" data-testid="digest-range-hint" className="pb-2 text-xs text-muted-foreground">
                    Up to {MAX_WINDOW_DAYS} days; changing one date may move the other
                </span>

                <span
                    data-testid="digest-range-adjustment"
                    role="status"
                    aria-live="polite"
                    className="sr-only"
                >
                    {rangeAdjustment}
                </span>

                {/* Sync freshness hint (Feature B): the hourly HostHub sync lags
                    real-time booking emails, so surface when it last ran. */}
                <span data-testid="digest-last-synced" className="pb-2 text-xs text-muted-foreground">
                    Last synced {shortTime(data?.lastSyncedAt)}
                </span>
            </div>

            {!isLoading && !isError && rangeTotals && (
                <Card data-testid="digest-headline" className="bg-white">
                    <CardContent className="flex items-center gap-3 py-4">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-700">
                            <CalendarCheck2 className="h-5 w-5" aria-hidden={true} />
                        </div>
                        <p className="text-lg font-semibold">{headline}</p>
                    </CardContent>
                </Card>
            )}

            {isLoading && <DigestSkeleton />}

            {!isLoading && isError && (
                <div data-testid="digest-error-panel" role="alert" className="flex flex-col items-center gap-4 py-8 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <XCircle className="h-6 w-6" aria-hidden={true} />
                    </div>
                    <p className="max-w-md text-sm text-slate-700">The daily digest could not be loaded.</p>
                    <Button data-testid="digest-retry" onClick={() => void refetch()}>
                        Retry
                    </Button>
                </div>
            )}

            {!isLoading && !isError && activeDays.length === 0 && (
                <div data-testid="digest-empty" className="flex flex-col items-center gap-3 py-12 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                        <Inbox className="h-6 w-6" aria-hidden={true} />
                    </div>
                    <p className="text-sm text-slate-700">No reservation activity in this period.</p>
                </div>
            )}

            {!isLoading && !isError && activeDays.length > 0 && (
                <div data-testid="digest-day-list" className="flex flex-col gap-4">
                    {activeDays.map((day) => (
                        <DayCard
                            key={formatDateToISODate(day.date instanceof Date ? day.date : new Date(day.date))}
                            day={day}
                            onOpenHistory={openHistory}
                        />
                    ))}
                </div>
            )}

            <ReservationHistoryDialog
                open={history !== null}
                reservationId={history?.reservationId ?? null}
                reservationCode={history?.reservationCode ?? ""}
                onClose={closeHistory}
            />
        </div>
    );
};

export default DailyDigestPage;
